import { useState } from 'react';
import axios from 'axios';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Department → badge colors (user-specified)
const DEPT_COLORS = {
  'Operations': 'bg-blue-500/15 text-blue-400 border-blue-500/40',
  'Control Room': 'bg-orange-500/15 text-orange-400 border-orange-500/40',
  'Payroll': 'bg-emerald-500/15 text-emerald-400 border-emerald-500/40',
  'HR & Compliance': 'bg-purple-500/15 text-purple-400 border-purple-500/40',
  'Recruitment & Onboarding': 'bg-teal-500/15 text-teal-400 border-teal-500/40',
  'Client Management': 'bg-yellow-500/15 text-yellow-500 border-yellow-500/40',
  'Quality & Audit': 'bg-gray-500/15 text-gray-400 border-gray-500/40',
  'Secondary Admin': 'bg-slate-500/15 text-slate-400 border-slate-500/40',
};

// Small colored indicator on records created/modified by a department.
// Click → full change history (who, department, field old→new, when).
// Primary-admin-only surface; records touched only by the primary admin
// have no dept_touch and render nothing.
export default function DeptBadge({ deptTouch, targetId, className = '' }) {
  const [history, setHistory] = useState(null);
  if (!deptTouch?.department) return null;
  const colors = DEPT_COLORS[deptTouch.department] || DEPT_COLORS['Secondary Admin'];

  const loadHistory = () => {
    if (history !== null) return;
    axios.get(`${API}/admin/change-log`, { params: { target_id: targetId } })
      .then((r) => setHistory(r.data?.items || []))
      .catch(() => setHistory([]));
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); loadHistory(); }}
          data-testid={`dept-badge-${targetId}`}
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border text-[9px] font-semibold uppercase tracking-wider whitespace-nowrap ${colors} ${className}`}
          title={`${deptTouch.department} — ${deptTouch.user_name || ''}`}
        >
          {deptTouch.department} • {deptTouch.action === 'created' ? 'CREATED' : 'MODIFIED'}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        onClick={(e) => e.stopPropagation()}
        className="bg-card border-border rounded-sm w-80 max-h-80 overflow-y-auto p-3 z-[70]"
        data-testid={`dept-history-${targetId}`}
      >
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Department change history</p>
        {history === null ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : history.length === 0 ? (
          <p className="text-xs text-muted-foreground">No entries.</p>
        ) : (
          <div className="space-y-2">
            {history.map((h) => (
              <div key={h.id} className="border border-border rounded-sm p-2">
                <div className="flex items-center justify-between gap-2">
                  <span className={`px-1 py-0.5 rounded-sm border text-[9px] uppercase font-semibold ${DEPT_COLORS[h.department] || DEPT_COLORS['Secondary Admin']}`}>
                    {h.department}
                  </span>
                  <span className="text-[10px] text-muted-foreground">{new Date(h.created_at).toLocaleString('en-GB')}</span>
                </div>
                <p className="text-xs mt-1">
                  <strong>{h.user_name}</strong> — {h.action}{h.label ? ` · ${h.label}` : ''}
                </p>
                {(h.changes || []).map((c, i) => (
                  <p key={i} className="text-[11px] text-muted-foreground mt-0.5">
                    <span className="text-foreground">{c.field}</span>:{' '}
                    <span className="line-through opacity-70">{String(c.old ?? '—')}</span>
                    {' → '}
                    <span className="text-emerald-400">{String(c.new ?? '—')}</span>
                  </p>
                ))}
              </div>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
